import React, { useCallback, useEffect, useState } from "react";
import { convertDateToYYYYMMDD, parseFlexibleDateToMMDDYYYY } from "../../../utils/date";
import Dropdown, { DropdownOption } from "../../../components/common/Dropdown";
import { ValidationError } from "../../../components/common/ErrorBanner";

type Props = {
  selectedRegionCode?: DropdownOption;
  regionOptions: DropdownOption[];
  entityOptions: DropdownOption[];
  areaOptions: DropdownOption[];
  centerOptions: DropdownOption[];
  locationOptions: DropdownOption[];
  costCenterOptions: DropdownOption[];
  ratingOptions: DropdownOption[];
  rating: DropdownOption[];
  results: DropdownOption[];
  valueCompass: DropdownOption[];
  testOfChangeRating: DropdownOption[];
  searchByOptions: DropdownOption[];
  onRevise?: () => void;
  onSearch?: (searchParams: any) => void;
  onRegionChange?: (option: DropdownOption | null) => void;
  onEntityChange?: (option: DropdownOption | null) => void;
  onAreaChange?: (option: DropdownOption | null) => void;
  onCenterChange?: (option: DropdownOption | null) => void;
  onLocationChange?: (option: DropdownOption | null) => void;
  onCostCenterChange?: (
    selectedIds: (string | number)[],
    isAllSelected: boolean,
  ) => void;
  onCostCenterSortBy?: (sortBy: string) => void;
  onRatingChange?: (
    selectedIds: (string | number)[],
    isAllSelected: boolean,
  ) => void;
  onValueCompassChange?: (
    selectedIds: (string | number)[],
    isAllSelected: boolean,
  ) => void;
  onMetrixChange?: (
    selectedIds: (string | number)[],
    isAllSelected: boolean,
  ) => void;
  onResultsChange?: (
    selectedIds: (string | number)[],
    isAllSelected: boolean,
  ) => void;
  onTestOfChangeRatingChange?: (
    selectedIds: (string | number)[],
    isAllSelected: boolean,
  ) => void;
  onProjectTypeChange?: (value: string) => void;
  onSearchByChange?: (option: DropdownOption | null) => void;
  onSearchByTextChange?: (text: string) => void;
  onFromIdChange?: (value: string) => void;
  onToIdChange?: (value: string) => void;
  onProjectNameChange?: (text: string) => void;
  selectedEntityCode?: DropdownOption;
  selectedAreaCode?: DropdownOption;
  selectedCenterCode?: DropdownOption;
  selectedLocationCode?: DropdownOption;
  selectedCostCenterCode?: {
    isAllSelected: boolean;
    selectedIds: (string | number)[];
  };
  selectedRating?: { isAllSelected: boolean; selectedIds: (string | number)[] };
  selectedValueCompass?: {
    isAllSelected: boolean;
    selectedIds: (string | number)[];
  };
  selectedMetrix?: { isAllSelected: boolean; selectedIds: (string | number)[] };
  selectedResults?: {
    isAllSelected: boolean;
    selectedIds: (string | number)[];
  };
  selectedTestOfChangeRating?: {
    isAllSelected: boolean;
    selectedIds: (string | number)[];
  };
  selectedProjectType?: string;
  selectedSearchBy?: DropdownOption;
  selectedSearchByText?: string;
  selectedFromId?: string;
  selectedToId?: string;
  selectedProjectName?: string;
  sortByCostCenter?: string;
  isLoadingData?: boolean;
  rangeStartDate?: string;
  rangeEndDate?: string;
  rangeStartError?: string;
  rangeEndError?: string;
  onRangeStartChange?: (name: string, value: string) => void;
  onRangeEndChange?: (name: string, value: string) => void;
  /** Externally supplied initial value for Min ROI (used for session restore). */
  minROI?: string;
  /** Externally supplied initial value for Max ROI (used for session restore). */
  maxROI?: string;
};

const defaultOption: DropdownOption = {
  id: 0,
  label: "All",
  value: "All",
};

const defaultRegionOption: DropdownOption = {
  id: "",
  label: "Select",
  value: "",
};

const getOptionCode = (option?: DropdownOption): string => {
  if (!option) return "All";
  const idValue = String(option.id ?? "").trim();
  if (idValue && idValue !== "0") {
    return idValue;
  }
  const optionValue = String(option.value ?? "").trim();
  return optionValue || "All";
};

const MAX_SAFE_INTEGER_VALUE = "9007199254740991";
const isUbtIdSearch = (option?: DropdownOption): boolean => option?.value === "UBTId";

const validateUbtIdValue = (value: string): string => {
  const trimmedValue = value.trim();
  if (!trimmedValue || /^[0-9%$]+$/.test(trimmedValue)) {
    const numericValue = trimmedValue.replaceAll(/[%$]/g, "");
    const normalizedValue = numericValue.replace(/^0+(?=\d)/, "");
    if (
      numericValue &&
      (normalizedValue.length > MAX_SAFE_INTEGER_VALUE.length ||
        (normalizedValue.length === MAX_SAFE_INTEGER_VALUE.length &&
          normalizedValue > MAX_SAFE_INTEGER_VALUE))
    ) {
      return `Search value must be less than or equal to ${MAX_SAFE_INTEGER_VALUE}.`;
    }
    return "";
  }
  return "Search value should be a non-negative number.";
};

const validateUbtIdRange = (fromId: string, toId: string): string => {
  const from = fromId.trim();
  const to = toId.trim();
  const isWithinMaxSafeInteger = (value: string): boolean => {
    const normalizedValue = value.replace(/^0+(?=\d)/, "");
    return normalizedValue.length < MAX_SAFE_INTEGER_VALUE.length ||
      (normalizedValue.length === MAX_SAFE_INTEGER_VALUE.length &&
        normalizedValue <= MAX_SAFE_INTEGER_VALUE);
  };
  if (from && !/^\d+$/.test(from)) return "From ID must be a non-negative whole number.";
  if (to && !/^\d+$/.test(to)) return "To ID must be a non-negative whole number.";
  if (from && !isWithinMaxSafeInteger(from)) {
    return `From ID must be less than or equal to ${MAX_SAFE_INTEGER_VALUE}.`;
  }
  if (to && !isWithinMaxSafeInteger(to)) {
    return `To ID must be less than or equal to ${MAX_SAFE_INTEGER_VALUE}.`;
  }
  if (from && to && BigInt(from) > BigInt(to)) {
    return "From ID must be less than or equal to To ID.";
  }
  return "";
};

const ID_RANGE_ORDER_ERROR = "From ID must be less than or equal to To ID.";

const getProjectTypeParam = (projectType: string): string | undefined => {
  const projectTypeMap: Record<string, string> = {
    Active: "active",
    Complete: "complete",
    Both: "both",
  };
  return projectTypeMap[projectType];
};

import AdvancedSearchSection from "./AdvancedSearchSection";
import PopUp from "../../../components/common/PopUp/PopUp";

const SearchProjectContainer: React.FC<Props> = React.memo(
  ({
    regionOptions,
    entityOptions,
    areaOptions,
    centerOptions,
    locationOptions,
    costCenterOptions,
    searchByOptions,
    ratingOptions,
    rating,
    results,
    valueCompass,
    testOfChangeRating,
    onSearch,
    onRegionChange,
    onEntityChange,
    onAreaChange,
    onCenterChange,
    onLocationChange,
    onCostCenterChange,
    onCostCenterSortBy,
    onRatingChange,
    onValueCompassChange,
    onMetrixChange,
    onResultsChange,
    onTestOfChangeRatingChange,
    onProjectTypeChange,
    onSearchByChange,
    onSearchByTextChange,
    onFromIdChange,
    onToIdChange,
    onProjectNameChange,
    selectedRegionCode,
    selectedEntityCode,
    selectedAreaCode,
    selectedCenterCode,
    selectedLocationCode,
    selectedCostCenterCode,
    selectedRating,
    selectedValueCompass,
    selectedMetrix,
    selectedResults,
    selectedTestOfChangeRating,
    selectedProjectType = "Active",
    selectedSearchBy,
    selectedSearchByText = "",
    selectedFromId = "",
    selectedToId = "",
    selectedProjectName = "",
    sortByCostCenter = "id",
    isLoadingData = false,
    // Aliased to avoid shadowing the local state variables below
    rangeStartDate: propRangeStartDate = "",
    rangeEndDate: propRangeEndDate = "",
    minROI: propMinROI = "",
    maxROI: propMaxROI = "",
  }) => {
    
    const effectiveSearchBy = selectedSearchBy ?? (searchByOptions.length > 0 ? searchByOptions[0] : {
      id: 0,
      label: "UBT Name",
      value: "UBTName",
    });
    const effectiveSearchByText = selectedSearchByText ?? "";

    // Keep local state for Project Name search and other fields not controlled by parent
    const [searchTextError, setSearchTextError] = useState("");
    const [fromIdError, setFromIdError] = useState("");
    const [toIdError, setToIdError] = useState("");
    const [searchByProjectNameText, setSearchByProjectNameText] = useState(selectedProjectName ?? "");
    const [searchByProjectNameError, setSearchByProjectNameError] =
      useState("");
    const [showAdvanced, setShowAdvanced] = useState(false);

    // Sync project-name local state from prop when Redux value changes
    useEffect(() => {
      setSearchByProjectNameText(selectedProjectName ?? "");
    }, [selectedProjectName]);

    // Date/ROI state — initialised from session-restore props on first mount.
    // A useEffect syncs from prop whenever a non-empty restore value arrives.
    const [rangeStartDate, setRangeStartDate] = useState(propRangeStartDate);
    const [rangeEndDate, setRangeEndDate] = useState(propRangeEndDate);
    const [rangeStartError, setRangeStartError] = useState("");
    const [rangeEndError, setRangeEndError] = useState("");
    const [minROI, setMinROI] = useState(propMinROI);
    const [maxROI, setMaxROI] = useState(propMaxROI);

    // Sync date/ROI local state from props when session-restore values arrive
    // after the initial render (propRangeStartDate etc. start as "" on first mount).
    useEffect(() => {
      if (propRangeStartDate) setRangeStartDate(propRangeStartDate);
    }, [propRangeStartDate]);
    useEffect(() => {
      if (propRangeEndDate) setRangeEndDate(propRangeEndDate);
    }, [propRangeEndDate]);
    useEffect(() => {
      if (propMinROI) setMinROI(propMinROI);
    }, [propMinROI]);
    useEffect(() => {
      if (propMaxROI) setMaxROI(propMaxROI);
    }, [propMaxROI]);

    const [validationErrors] = useState<ValidationError[]>([]);

    // Date validation: not older than 5 years, not future dates.
    // Accepts flexible input formats (MM-DD-YYYY, MM/DD/YYYY, M-D-YY, etc.)
    const validateDate = (
      dateStr: string,
    ): { isValid: boolean; error: string } => {
      if (!dateStr) return { isValid: true, error: "" };

      // Normalize any accepted flexible format to MM-DD-YYYY first
      const normalized = parseFlexibleDateToMMDDYYYY(dateStr);
      if (!normalized) {
        return { isValid: false, error: "Invalid date format. Use MM-DD-YYYY" };
      }

      const parts = normalized.split("-").map((p) => Number.parseInt(p, 10));
      const [month, day, year] = parts;
      const inputDate = new Date(year, month - 1, day);

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (inputDate > today) {
        return { isValid: false, error: "This value is out of range" };
      }

      const fiveYearsAgo = new Date();
      fiveYearsAgo.setFullYear(fiveYearsAgo.getFullYear() - 5);
      fiveYearsAgo.setHours(0, 0, 0, 0);

      if (inputDate < fiveYearsAgo) {
        return { isValid: false, error: "This value is out of range" };
      }

      return { isValid: true, error: "" };
    };

    // Validate search text using the same character-count rule as Find and Select.
    const validateSearchText = (text: string): string => {
      const trimmedText = text.trim();
      if (!trimmedText) return "";

      if (trimmedText.length < 3) {
        return "Your search must contain at least 3 alpha-numeric characters.";
      }

      return "";
    };

    const handleRangeStartChange = (_name: string, value: string) => {
      setRangeStartDate(value);
      // Clear error when the field is emptied OR when the new value is a valid date
      // (e.g. the user picks a date from the calendar after having typed an invalid format).
      // This also clears any stale range-order error that lives on the start field.
      if (!value || validateDate(value).isValid) setRangeStartError('');
    };

    const handleRangeEndChange = (_name: string, value: string) => {
      setRangeEndDate(value);
      if (!value || validateDate(value).isValid) {
        setRangeEndError('');
        // Clear any stale range-order error from the start field when the end date changes.
        setRangeStartError((prev) =>
          prev.includes('is greater than') ? '' : prev,
        );
      }
    };

    /**
     * Checks that rangeStartDate is not later than rangeEndDate.
     * Requires both dates to be non-empty and individually valid.
     * Sets an error on the Range Start Activity field when the order is invalid,
     * matching the format: "Start date {X} is greater than end date {Y}."
     */
    const validateDateRangeOrder = useCallback(
      (startStr: string, endStr: string): boolean => {
        if (!startStr || !endStr) return true;

        const normalizedStart = parseFlexibleDateToMMDDYYYY(startStr);
        const normalizedEnd = parseFlexibleDateToMMDDYYYY(endStr);
        if (!normalizedStart || !normalizedEnd) return true; // individual errors shown separately

        const toDate = (mmddyyyy: string) => {
          const [m, d, y] = mmddyyyy.split('-').map(Number);
          return new Date(y, m - 1, d);
        };

        if (toDate(normalizedStart) > toDate(normalizedEnd)) {
          setRangeStartError(`${startStr} is greater than ${endStr}.`);
          return false;
        }

        // Clear any stale range-order error on the start field when order is now valid
        setRangeStartError((prev) =>
          prev.includes('is greater than') ? '' : prev,
        );
        return true;
      },
      [],
    );

    const handleRangeStartBlur = () => {
      const validation = validateDate(rangeStartDate);
      setRangeStartError(validation.error);
      if (!validation.error) {
        validateDateRangeOrder(rangeStartDate, rangeEndDate);
      }
    };

    const handleRangeEndBlur = () => {
      const validation = validateDate(rangeEndDate);
      setRangeEndError(validation.error);
      if (!validation.error) {
        validateDateRangeOrder(rangeStartDate, rangeEndDate);
      }
    };

    const handleSearchTextChange = (value: string) => {
      const error = isUbtIdSearch(effectiveSearchBy)
        ? validateUbtIdValue(value)
        : validateSearchText(value);
      setSearchTextError(error);
      onSearchByTextChange?.(value);
    };

    const handleSearchByChange = (option: DropdownOption) => {
      setSearchTextError("");
      setFromIdError("");
      setToIdError("");
      onSearchByChange?.(option);
    };

    const handleFromIdChange = (value: string) => {
      const rangeError = validateUbtIdRange(value, selectedToId);
      const orderError = rangeError === ID_RANGE_ORDER_ERROR ? rangeError : "";
      setFromIdError(orderError || validateUbtIdRange(value, ""));
      setToIdError(orderError || validateUbtIdRange("", selectedToId));
      onFromIdChange?.(value);
    };

    const handleToIdChange = (value: string) => {
      const rangeError = validateUbtIdRange(selectedFromId, value);
      const orderError = rangeError === ID_RANGE_ORDER_ERROR ? rangeError : "";
      setToIdError(orderError || validateUbtIdRange("", value));
      setFromIdError(orderError || validateUbtIdRange(selectedFromId, ""));
      onToIdChange?.(value);
    };

    const handleSearchByProjectNameChange = (value: string) => {
      setSearchByProjectNameText(value);
      const error = validateSearchText(value);
      setSearchByProjectNameError(error);
      onProjectNameChange?.(value);
    };

    const hasDateErrors = rangeStartError !== "" || rangeEndError !== "";
    const hasSearchErrors =
      searchTextError !== "" || searchByProjectNameError !== "";
    const hasIdErrors = fromIdError !== "" || toIdError !== "";

    // Use props instead of local state for dropdowns
    const safeSelectedRegionCode = selectedRegionCode ?? defaultRegionOption;
    const safeSelectedEntityCode = selectedEntityCode ?? defaultOption;
    const safeSelectedAreaCode = selectedAreaCode ?? defaultOption;
    const safeSelectedCenterCode = selectedCenterCode ?? defaultOption;
    const safeSelectedLocationCode = selectedLocationCode ?? defaultOption;
    const safeSelectedCostCenterCode = selectedCostCenterCode ?? {
      isAllSelected: true,
      selectedIds: [],
    };
    const safeSelectedRating = selectedRating ?? {
      isAllSelected: true,
      selectedIds: [],
    };
    const safeSelectedValueCompass = selectedValueCompass ?? {
      isAllSelected: true,
      selectedIds: [],
    };
    const safeSelectedMetrix = selectedMetrix ?? {
      isAllSelected: true,
      selectedIds: [],
    };
    const safeSelectedResults = selectedResults ?? {
      isAllSelected: true,
      selectedIds: [],
    };
    const safeSelectedTestOfChangeRating = selectedTestOfChangeRating ?? {
      isAllSelected: true,
      selectedIds: [],
    };

    // Helper function to format multi-select dropdown values

    // Helper function to format multi-select dropdown IDs
    const formatMultiSelectIds = useCallback(
        /**
         * Formats multi-select dropdown values as a '~'-joined string of IDs.
         * Used for all API fields that expect IDs, not labels.
         * @param selected - Selected state object with isAllSelected and selectedIds
         * @param options - Dropdown options for the field
         * @param onlyNumeric - If true, only numeric IDs are included (for costCenter)
         * @returns '~'-joined string of IDs, or 'All' if all selected
         */
      (
        selected: { isAllSelected: boolean; selectedIds: (string | number)[] },
        options: DropdownOption[],
        onlyIds: boolean = false
      ): string => {
        if (selected.isAllSelected) {
          return "All";
        }

        // Filter out "All" options
        const filteredOptions = options.filter(
          (opt) => opt.value !== "All" && opt.label !== "All",
        );

        // Filter out invalid IDs (empty strings, null, undefined) but allow 0 and '0'
        let validIds = selected.selectedIds.filter(
          (id) => id !== null && id !== undefined && id !== "",
        );

        // For costCenter, map selectedIds to the corresponding option's id (from costCenterOptions)
        if (onlyIds) {
          validIds = validIds.map((selectedId) => {
            const match = options.find((opt) => opt.id === selectedId);
            return match ? match.id : selectedId;
          });
        }

        // Check if all items are selected (by comparing count)
        if (
          filteredOptions.length > 0 &&
          validIds.length === filteredOptions.length
        ) {
          return "All";
        }

        // Return the valid IDs directly, joined by ~
        return validIds.length > 0 ? validIds.join("~") : "All";
      },
      [],
    );

    const validateSearchClick = useCallback((): boolean => {
      const startError = validateDate(rangeStartDate).error;
      const endError = validateDate(rangeEndDate).error;
      setRangeStartError(startError);
      setRangeEndError(endError);
      if (startError || endError) return false;

      const idError = isUbtIdSearch(effectiveSearchBy)
        ? validateUbtIdRange(selectedFromId, selectedToId)
        : "";
      const searchValueError = isUbtIdSearch(effectiveSearchBy)
        ? validateUbtIdValue(effectiveSearchByText)
        : "";
      setFromIdError(idError);
      setToIdError(idError);
      setSearchTextError(searchValueError);
      if (idError || searchValueError) return false;

      return validateDateRangeOrder(rangeStartDate, rangeEndDate);
    }, [
      effectiveSearchBy,
      effectiveSearchByText,
      rangeEndDate,
      rangeStartDate,
      selectedFromId,
      selectedToId,
      validateDateRangeOrder,
    ]);

    const buildSearchParams = useCallback((): Record<string, any> => {
      const projectNameQuery = searchByProjectNameText.trim();
      const costCenterValue = safeSelectedCostCenterCode.isAllSelected
        ? "All"
        : safeSelectedCostCenterCode.selectedIds
            .map((id) => typeof id === "string" && id.includes("-") ? id.split("-")[0] : id)
            .join("~");
      const searchParams: Record<string, any> = {
        region: getOptionCode(safeSelectedRegionCode),
        sortColumn: "ubtId",
        entity: getOptionCode(safeSelectedEntityCode),
        area: getOptionCode(safeSelectedAreaCode),
        center: getOptionCode(safeSelectedCenterCode),
        location: getOptionCode(safeSelectedLocationCode),
        sortBy: sortByCostCenter === "id" ? "DEPTID" : "DEPTNAME",
        costCenter: costCenterValue,
        focusArea: formatMultiSelectIds(safeSelectedRating, rating),
        metric: formatMultiSelectIds(safeSelectedMetrix, ratingOptions),
        valueCompassDesc: formatMultiSelectIds(safeSelectedValueCompass, valueCompass),
        result: formatMultiSelectIds(safeSelectedResults, results),
        rating: formatMultiSelectIds(safeSelectedTestOfChangeRating, testOfChangeRating),
        searchBy: effectiveSearchBy?.value || "UBTName",
        searchString: effectiveSearchByText || undefined,
        searchText: effectiveSearchByText || projectNameQuery || undefined,
        fromId: isUbtIdSearch(effectiveSearchBy) ? selectedFromId || undefined : undefined,
        toId: isUbtIdSearch(effectiveSearchBy) ? selectedToId || undefined : undefined,
        searchByProject: projectNameQuery || undefined,
        projectName: projectNameQuery || undefined,
        startDate: rangeStartDate ? convertDateToYYYYMMDD(rangeStartDate) : undefined,
        endDate: rangeEndDate ? convertDateToYYYYMMDD(rangeEndDate) : undefined,
        minROI: minROI || undefined,
        maxROI: maxROI || undefined,
        rangeStartDateRaw: rangeStartDate || undefined,
        rangeEndDateRaw: rangeEndDate || undefined,
        page: 1,
        perPage: 20,
      };
      const projectType = getProjectTypeParam(selectedProjectType);
      if (projectType) searchParams.projectType = projectType;
      return searchParams;
    }, [
      effectiveSearchBy,
      effectiveSearchByText,
      formatMultiSelectIds,
      maxROI,
      minROI,
      rangeEndDate,
      rangeStartDate,
      rating,
      ratingOptions,
      results,
      safeSelectedAreaCode,
      safeSelectedCenterCode,
      safeSelectedCostCenterCode,
      safeSelectedEntityCode,
      safeSelectedLocationCode,
      safeSelectedMetrix,
      safeSelectedRating,
      safeSelectedResults,
      safeSelectedRegionCode,
      safeSelectedTestOfChangeRating,
      safeSelectedValueCompass,
      searchByProjectNameText,
      selectedFromId,
      selectedProjectType,
      selectedToId,
      sortByCostCenter,
      testOfChangeRating,
      valueCompass,
    ]);

    const handleSearchClick = useCallback(() => {
      if (!validateSearchClick() || !onSearch) return;
      onSearch(buildSearchParams());
    }, [buildSearchParams, onSearch, validateSearchClick]);

    return (
      <div>
        <div className="flex gap-x-3 mt-8! mb-0! h4">
          <label htmlFor="region" className="">
            Your Current Region:
          </label>
          <div className="">
            <input
              className="w-full!"
              id="region"
              name="region"
              type="text"
              value={safeSelectedRegionCode?.label || "Select"}
              disabled
            />
          </div>
        </div>

        <div className="flex gap-x-8 mt-8!">
          <div className="w-1/4!">
            <Dropdown
              label="Search Criteria"
              selectedValue={effectiveSearchBy}
              options={
                searchByOptions.length ? searchByOptions : [defaultOption]
              }
              onSelect={handleSearchByChange}
              buttonClassName="w-full!"
              menuClassName="min-w-60 w-full!"
              popupClassName="-medium"
              popupTitle="Enter a wildcard search(must be at least 3 alpha-numeric characters), for example:"
              popupMessage="<ul class='list-disc! list-inside!'> <li>%Safety to find information that contains the word Safety somewhere in the field.</li> <li> Safety to find information that begins with the word Safety.</li></ul>"
            />
          </div>

          <div
            className="ds-form w-1/2!"
            data-ds-theme="vessel"
            data-ds-variant="input"
          >
            <label htmlFor="text-input">Search Value</label>
            <input
              autoComplete="on"
              className={`mt-1! w-1/2! ${searchTextError ? "border-red-500" : ""}`}
              id="text-input"
              type="text"
              value={effectiveSearchByText}
              onChange={(e) => handleSearchTextChange(e.target.value)}
              aria-invalid={searchTextError ? "true" : "false"}
              aria-describedby={
                searchTextError ? "search-text-error" : undefined
              }
            />
            {searchTextError && (
              <div
                className="text-red-600 text-sm mt-1"
                id="search-text-error"
                role="alert"
              >
                {searchTextError}
              </div>
            )}
          </div>
        </div>

        {isUbtIdSearch(effectiveSearchBy) && (
          <div className="flex gap-x-8 mt-8!">
            <div className="ds-form w-1/4!" data-ds-theme="vessel" data-ds-variant="input">
              <label htmlFor="from-id-input">From ID</label>
              <input
                id="from-id-input"
                className={`mt-1! w-full! ${fromIdError ? "border-red-500" : ""}`}
                type="text"
                value={selectedFromId}
                onChange={(event) => handleFromIdChange(event.target.value)}
              />
              {fromIdError && <div className="text-red-600 text-sm mt-1" role="alert">{fromIdError}</div>}
            </div>
            <div className="ds-form w-1/4!" data-ds-theme="vessel" data-ds-variant="input">
              <label htmlFor="to-id-input">To ID</label>
              <input
                id="to-id-input"
                className={`mt-1! w-full! ${toIdError ? "border-red-500" : ""}`}
                type="text"
                value={selectedToId}
                onChange={(event) => handleToIdChange(event.target.value)}
              />
              {toIdError && <div className="text-red-600 text-sm mt-1" role="alert">{toIdError}</div>}
            </div>
          </div>
        )}

        <div
          className="ds-form w-1/2! mt-8!"
          data-ds-theme="vessel"
          data-ds-variant="input"
        >
          <div className="flex items-center gap-x-1!">
            <label className="mb-[-2px]!" htmlFor="project-name-input">
              And/or search by project names
            </label>
            <PopUp
              headerText="Enter a wildcard search(must be at least 3 alpha-numeric characters), for example:"
              message='<ul class="list-disc! list-inside!"> <li>%Safety to find information that contains the word Safety somewhere in the field.</li> <li> Safety to find information that begins with the word Safety.</li></ul>'
            ></PopUp>
          </div>
          <input
            autoComplete="on"
            className={`mt-1! w-1/2! ${searchByProjectNameError ? "border-red-500" : ""}`}
            id="project-name-input"
            type="text"
            value={searchByProjectNameText}
            onChange={(e) => handleSearchByProjectNameChange(e.target.value)}
            aria-invalid={searchByProjectNameError ? "true" : "false"}
            aria-describedby={
              searchByProjectNameError ? "project-name-error" : undefined
            }
          />
          {searchByProjectNameError && (
            <div
              className="text-red-600 text-sm mt-1"
              id="project-name-error"
              role="alert"
            >
              {searchByProjectNameError}
            </div>
          )}
        </div>

        <div className="w-full! mt-8!">
          <button
            onClick={() => setShowAdvanced((s) => !s)}
            className="inline-flex items-center justify-center gap-2 border-0 bg-transparent p-0 align-middle"
            aria-label="Advance Search"
          >
            <img
              src={
                showAdvanced
                  ? "/assets/images/minus-circle-outline.svg"
                  : "/assets/images/plus-circle-outline.svg"
              }
              alt={
                showAdvanced
                  ? "Collapse advanced search"
                  : "Expand advanced search"
              }
              className="h-6! w-6!"
            />

            <span className="text-sm h4 leading-tight m-0 p-0">
              Advanced search options
            </span>
          </button>
        </div>

        <AdvancedSearchSection
          show={showAdvanced}
          regionOptions={regionOptions}
          entityOptions={entityOptions}
          areaOptions={areaOptions}
          centerOptions={centerOptions}
          locationOptions={locationOptions}
          costCenterOptions={costCenterOptions}
          ratingOptions={ratingOptions}
          rating={rating}
          results={results}
          valueCompass={valueCompass}
          testOfChangeRating={testOfChangeRating}
          handleRegion={onRegionChange || (() => {})}
          handleEntity={onEntityChange || (() => {})}
          handleArea={onAreaChange || (() => {})}
          handleCenter={onCenterChange || (() => {})}
          handleLocation={onLocationChange || (() => {})}
          handleCostCenterChange={(selectedIds, isAllSelected) =>
            onCostCenterChange?.(selectedIds, isAllSelected)
          }
          handleCostCenterSortBy={onCostCenterSortBy || (() => {})}
          handleRatingChange={(selectedIds, isAllSelected) =>
            onRatingChange?.(selectedIds, isAllSelected)
          }
          handleValueCompassChange={(selectedIds, isAllSelected) =>
            onValueCompassChange?.(selectedIds, isAllSelected)
          }
          handleMetrixChange={(selectedIds, isAllSelected) =>
            onMetrixChange?.(selectedIds, isAllSelected)
          }
          handleResultsChange={(selectedIds, isAllSelected) =>
            onResultsChange?.(selectedIds, isAllSelected)
          }
          handleTestOfChangeRatingChange={(selectedIds, isAllSelected) =>
            onTestOfChangeRatingChange?.(selectedIds, isAllSelected)
          }
          handleProjectTypeChange={(value) => onProjectTypeChange?.(value)}
          selectedEntityCode={safeSelectedEntityCode}
          selectedRegionCode={safeSelectedRegionCode}
          selectedAreaCode={safeSelectedAreaCode}
          selectedCenterCode={safeSelectedCenterCode}
          selectedLocationCode={safeSelectedLocationCode}
          selectedCostCenterCode={safeSelectedCostCenterCode}
          selectedRating={safeSelectedRating}
          selectedValueCompass={safeSelectedValueCompass}
          selectedMetrix={safeSelectedMetrix}
          selectedResults={safeSelectedResults}
          selectedTestOfChangeRating={safeSelectedTestOfChangeRating}
          selectedProjectType={selectedProjectType}
          sortByCostCenter={sortByCostCenter}
          isLoadingData={isLoadingData}
          validationErrors={validationErrors}
          rangeStartDate={rangeStartDate}
          rangeEndDate={rangeEndDate}
          rangeStartError={rangeStartError}
          rangeEndError={rangeEndError}
          onRangeStartChange={handleRangeStartChange}
          onRangeEndChange={handleRangeEndChange}
          onRangeStartBlur={handleRangeStartBlur}
          onRangeEndBlur={handleRangeEndBlur}
          minROI={minROI}
          maxROI={maxROI}
          onMinROIChange={(e) => setMinROI(e.target.value)}
          onMaxROIChange={(e) => setMaxROI(e.target.value)}
        />

        <div className="flex gap-x-4 mt-8! mb-4!">
          <button
            className="button py-0! -primary -light"
            data-analytics-type="button"
            data-analytics-click="Default"
            disabled={hasDateErrors || hasSearchErrors || hasIdErrors}
            onClick={handleSearchClick}
          >
            Search
          </button>
        </div>
      </div>
    );
  },
);

export default SearchProjectContainer;
